import { useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Circle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Modal that drives a manual daily-refresh run and visualizes progress in
 * real time. Opens a fetch streaming connection to
 * ``POST /api/renewals/refresh/stream`` and renders each step as it lands.
 *
 * Why fetch + ReadableStream instead of EventSource:
 *   - EventSource is GET-only, but admin-protected mutations should be POST.
 *   - Fetch with streaming body lets us POST + read SSE-formatted lines.
 *   - Credentials flow the same way the rest of the FE uses (cookie).
 */

type StepKey =
  | "cache_invalidation"
  | "csm_sync"
  | "contract_metadata"
  | "session_rollover";

type StepStatus = "pending" | "running" | "done" | "error";

interface StepState {
  status: StepStatus;
  detail: unknown;
}

interface ProgressEvent {
  step: StepKey | "summary" | "fatal";
  status?: "started" | "done" | "error";
  detail?: unknown;
}

const STEPS: { key: StepKey; label: string; describe: (detail: unknown) => string }[] = [
  {
    key: "cache_invalidation",
    label: "Refresh data caches",
    describe: (d) => {
      const o = d as { keys_invalidated?: number; keys_total?: number } | undefined;
      if (!o) return "Drop the renewals snowflake caches so the next view is fresh.";
      return `${o.keys_invalidated ?? 0} / ${o.keys_total ?? 0} caches cleared.`;
    },
  },
  {
    key: "csm_sync",
    label: "Sync CSM directory",
    describe: (d) => {
      const o = d as { created?: number; updated?: number; total?: number } | undefined;
      if (!o) return "Re-pull HubSpot owners and reconcile against the user table.";
      const parts: string[] = [];
      if (o.created != null) parts.push(`${o.created} new`);
      if (o.updated != null) parts.push(`${o.updated} updated`);
      if (o.total != null) parts.push(`${o.total} total`);
      return parts.length > 0 ? parts.join(" · ") : "Directory synced.";
    },
  },
  {
    key: "contract_metadata",
    label: "Backfill contract metadata",
    describe: (d) => {
      const o = d as {
        contracts_seen?: number;
        inserted?: number;
        refreshed?: number;
        skipped_manual?: number;
      } | undefined;
      if (!o) return "Walk every account, seed term_type heuristics for any new contracts.";
      const parts: string[] = [];
      if (o.contracts_seen != null) parts.push(`${o.contracts_seen} contracts seen`);
      if (o.inserted) parts.push(`${o.inserted} new`);
      if (o.refreshed) parts.push(`${o.refreshed} refreshed`);
      if (o.skipped_manual) parts.push(`${o.skipped_manual} kept (manual override)`);
      return parts.length > 0 ? parts.join(" · ") : "No changes.";
    },
  },
  {
    key: "session_rollover",
    label: "Roll over past-due renewal cycles",
    describe: (d) => {
      const o = d as {
        checked?: number;
        closed?: number;
        opened?: number;
      } | undefined;
      if (!o) return "Close past-anniversary sessions and open the next cycle.";
      if (o.checked === 0) return "No past-due sessions — every account is current.";
      return `${o.checked ?? 0} checked · ${o.closed ?? 0} closed · ${o.opened ?? 0} opened.`;
    },
  },
];

function StatusIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case "pending":
      return <Circle className="h-4 w-4 text-muted-foreground" />;
    case "running":
      return <Loader2 className="h-4 w-4 animate-spin text-info" />;
    case "done":
      return <CheckCircle2 className="h-4 w-4 text-success" />;
    case "error":
      return <AlertTriangle className="h-4 w-4 text-danger" />;
  }
}

export function RefreshProgressDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [steps, setSteps] = useState<Record<StepKey, StepState>>(() => initialState());
  const [running, setRunning] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [finishedAt, setFinishedAt] = useState<Date | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Auto-start on first open. Each subsequent open of the dialog (e.g. user
  // clicked Refresh again) gets a fresh run. Reset state at open time so the
  // user doesn't see stale checkmarks while we wait for the first event.
  useEffect(() => {
    if (!open) return;
    setSteps(initialState());
    setRunning(true);
    setFatalError(null);
    setFinishedAt(null);

    const controller = new AbortController();
    abortRef.current = controller;
    run(controller.signal, setSteps, setFatalError, setRunning, setFinishedAt, qc).catch(
      (err) => {
        if (err.name === "AbortError") return;
        setFatalError(String(err));
        setRunning(false);
      },
    );
    return () => {
      controller.abort();
    };
  }, [open, qc]);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(v) => !running && onOpenChange(v)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Daily refresh</DialogTitle>
          <DialogDescription>
            Running the same job APScheduler fires at 08:00 America/Denver.
            Safe to run mid-day — every step is idempotent.
          </DialogDescription>
        </DialogHeader>
        <ol className="flex flex-col gap-3 py-2">
          {STEPS.map((spec) => {
            const state = steps[spec.key];
            return (
              <li key={spec.key} className="flex items-start gap-3">
                <div className="mt-0.5">
                  <StatusIcon status={state.status} />
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className={cn(
                      "text-sm font-medium",
                      state.status === "pending" && "text-muted-foreground",
                    )}
                  >
                    {spec.label}
                  </div>
                  <div
                    className={cn(
                      "mt-0.5 text-xs",
                      state.status === "error"
                        ? "text-danger"
                        : "text-muted-foreground",
                    )}
                  >
                    {state.status === "error"
                      ? String(state.detail ?? "Step failed.")
                      : spec.describe(state.detail)}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
        {fatalError && (
          <div className="rounded-md border border-danger/30 bg-danger-light/40 p-2 text-xs text-danger">
            {fatalError}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {running
              ? "Don't close the dialog — the job is still running."
              : finishedAt
                ? `Finished ${finishedAt.toLocaleTimeString()}`
                : ""}
          </div>
          <Button
            variant={running ? "outline" : "default"}
            onClick={handleClose}
            disabled={running}
          >
            {running ? "Running…" : "Done"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function initialState(): Record<StepKey, StepState> {
  return {
    cache_invalidation: { status: "pending", detail: undefined },
    csm_sync: { status: "pending", detail: undefined },
    contract_metadata: { status: "pending", detail: undefined },
    session_rollover: { status: "pending", detail: undefined },
  };
}

async function run(
  signal: AbortSignal,
  setSteps: React.Dispatch<React.SetStateAction<Record<StepKey, StepState>>>,
  setFatalError: (s: string | null) => void,
  setRunning: (v: boolean) => void,
  setFinishedAt: (d: Date | null) => void,
  qc: ReturnType<typeof useQueryClient>,
) {
  const base = import.meta.env.VITE_API_BASE ?? "/api";
  const resp = await fetch(`${base}/renewals/refresh/stream`, {
    method: "POST",
    credentials: "include",
    signal,
  });
  if (!resp.ok) {
    setFatalError(`${resp.status} ${resp.statusText}`);
    setRunning(false);
    return;
  }
  if (!resp.body) {
    setFatalError("Server returned no event stream.");
    setRunning(false);
    return;
  }
  const reader = resp.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    // SSE events are separated by blank lines. Each "data: ..." line is a
    // single JSON event in our protocol — we don't use multi-line data.
    let idx = buffer.indexOf("\n\n");
    while (idx !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = chunk.split("\n").find((l) => l.startsWith("data: "));
      if (line) {
        const json = line.slice("data: ".length);
        try {
          const event = JSON.parse(json) as ProgressEvent;
          applyEvent(event, setSteps);
        } catch {
          /* tolerate bad chunk; the server only emits valid JSON */
        }
      }
      idx = buffer.indexOf("\n\n");
    }
  }
  setRunning(false);
  setFinishedAt(new Date());
  // Refresh affects every renewals-scoped query the FE has — invalidate
  // broadly so the user sees the new state when they close the dialog.
  void qc.invalidateQueries({ queryKey: ["renewals"] });
}

function applyEvent(
  event: ProgressEvent,
  setSteps: React.Dispatch<React.SetStateAction<Record<StepKey, StepState>>>,
) {
  if (event.step === "summary" || event.step === "fatal") return;
  const key = event.step as StepKey;
  const status: StepStatus =
    event.status === "started"
      ? "running"
      : event.status === "done"
        ? "done"
        : event.status === "error"
          ? "error"
          : "pending";
  setSteps((prev) => ({
    ...prev,
    [key]: { status, detail: event.detail },
  }));
}
